// /onboarding/first-diary — W2 §4.3 step 3 (write first diary).
//
// Wraps the shared ``DiaryEditor`` with the ``OnboardingShell current=3``.
// On successful create we redirect to /dashboard.  Tries to nudge
// inspiration in the placeholder text.

import { useNavigate } from "react-router-dom";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { PageHero } from "@/components/primitives/PageHero";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api/pair";
import { createDiary, type DiaryCreateInput, type DiaryUpdateInput } from "@/lib/api/diary";
import { diaryErrorMessage } from "@/lib/i18n/diary";
import { DiaryEditor } from "@/components/diary/DiaryEditor";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { useState } from "react";
import { FormErrorBanner } from "@/components/auth/FormField";
import { queryKeys } from "@/lib/queries/keys";

export function FirstDiaryPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  // W35.6: step 3 of the wizard docks the save action and hides the PillNav
  // so the flow stays immersive — the step rail at the top still shows
  // progress, and the back button below exits to the dashboard.
  useHidePillNav();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (payload: DiaryCreateInput | DiaryUpdateInput) => {
    setBusy(true);
    setError(null);
    try {
      await createDiary(payload as DiaryCreateInput);
      await qc.invalidateQueries({ queryKey: queryKeys.diary.all });
      await qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      nav("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError(diaryErrorMessage(err.code, err.message));
      else setError("日記建立失敗, 請稍後再試");
    } finally {
      setBusy(false);
    }
  };

  return (
    <OnboardingShell current={3} canSkip={user?.email_verified_at !== null}>
      <div data-testid="first-diary-page">
        <PageHero
          eyebrow="歡迎來到 heshe"
          title="寫下第一篇日記"
          subtitle="把今天的感受留給未來的自己, 把這一刻送給對方。"
        />
        <FormErrorBanner message={error} />
        <Card>
          <div className="p-6" data-testid="first-diary-card">
            <DiaryEditor
              mode="create"
              busy={busy}
              onSubmit={onSubmit}
              back={{
                label: "返回儀表板",
                onClick: () => nav("/dashboard"),
                testId: "first-diary-back",
              }}
            />
          </div>
        </Card>
      </div>
    </OnboardingShell>
  );
}
